import type { DistribucionLote, Lote } from '../api/types';
import { cop, num, pct } from '../lib/format';
import { MARGEN_OBJETIVO_DEFAULT, precioSugerido as calcSugerido } from '../lib/finanzas';

interface Props {
  lote: Lote;
  margenObjetivo?: number; // 0-1 (ej. 0.30 = 30%)
  compact?: boolean; // versión reducida para dashboard
  distribucion?: DistribucionLote | null;
}

/**
 * Calcula la distribución del ingreso de un lote en 4 buckets + ganancia.
 * Usa snapshots si están disponibles; si no, cae a un desglose simplificado
 * (todos los variables como "reinversión").
 */
export function distribucionDesdeLote(l: Lote): DistribucionLote {
  const paquetes = l.paquetes;
  const ingreso = Number(l.ingresoTotal);
  const ganancia = Number(l.gananciaTotal);
  const costosFijos = Number(l.costoFijoLote ?? paquetes * Number(l.costoFijoPaq));

  let manoObra = 0;
  let reinversion = 0;
  let servicios = 0;

  const snaps = l.snapshots ?? [];
  if (snaps.length > 0) {
    for (const s of snaps) {
      const monto = Number(s.cantidad) * Number(s.precioUsado);
      const nombre = s.insumo?.nombre ?? '';
      if (nombre === 'Mano de obra') manoObra += monto;
      else if (nombre === 'Agua' || nombre === 'Luz') servicios += monto;
      else reinversion += monto; // maíz, gas, bolsas
    }
  } else {
    // Sin snapshots: aproximamos usando el costo variable total del lote
    reinversion = Number(l.costoVariableLote ?? paquetes * Number(l.costoVariablePaq));
  }

  return { ingreso, manoObra, reinversion, servicios, costosFijos, ganancia };
}

/**
 * Bloque financiero estilo "Indicadores clave" del Excel original:
 * - Costos/paq (variable, fijo, total)
 * - Precio venta, ganancia, margen real vs objetivo, precio sugerido
 * - Ingresos, costo y utilidad del lote
 */
export function IndicadoresFinancieros({ lote, margenObjetivo = MARGEN_OBJETIVO_DEFAULT, compact = false, distribucion }: Props) {
  const costoVar = Number(lote.costoVariablePaq);
  const costoFijo = Number(lote.costoFijoPaq);
  const costoTotal = Number(lote.costoTotalPaq);
  const precio = Number(lote.precioVentaPaq);
  const ganancia = Number(lote.gananciaPaq);
  const margen = Number(lote.margenPct);
  const ingreso = Number(lote.ingresoTotal);
  const costoLote = Number(lote.costoTotalLote);
  const utilidad = Number(lote.gananciaTotal);
  const sugerido = calcSugerido(costoTotal, margenObjetivo);
  const diffPrecio = precio - sugerido;
  const paquetes = lote.paquetes;

  return (
    <div className="space-y-3">
      {/* Indicadores clave */}
      <Seccion titulo="Indicadores clave">
        <Row label="Costo variable/paq" value={cop(costoVar)} />
        <Row label="Costo fijo/paq" value={cop(costoFijo)} />
        <Row label="COSTO TOTAL/paq" value={cop(costoTotal)} strong />
        <Row label="Precio venta" value={cop(precio)} />
        <Row
          label="Ganancia/paq"
          value={cop(ganancia)}
          tone={ganancia >= 0 ? 'positive' : 'negative'}
        />
        <Row
          label="Margen real"
          value={pct(margen)}
          tone={margen >= margenObjetivo ? 'positive' : margen >= 0 ? 'warn' : 'negative'}
        />
        <Row label="Margen objetivo" value={pct(margenObjetivo)} muted />
        <Row
          label="Precio sugerido"
          value={cop(sugerido)}
          hint={
            diffPrecio < -1
              ? `Precio actual ${cop(Math.abs(diffPrecio))} por debajo`
              : diffPrecio > 1
                ? `Precio actual ${cop(diffPrecio)} por encima`
                : 'Precio actual alineado'
          }
          strong
        />
      </Seccion>

      {!compact && (
        <Seccion titulo="Ingresos del lote">
          <Row label="Paquetes producidos" value={num(paquetes, 0)} />
          <Row label="Ingreso al precio actual" value={cop(ingreso)} tone="positive" strong />
          <Row label="Costo total del lote" value={cop(costoLote)} tone="negative" />
          <Row
            label="Utilidad del lote"
            value={cop(utilidad)}
            tone={utilidad >= 0 ? 'positive' : 'negative'}
            strong
          />
        </Seccion>
      )}

      {!compact && distribucion && (
        <Seccion titulo="Distribución del ingreso">
          <Row label="Ingreso total" value={cop(distribucion.ingreso)} strong />
          <Row
            label="👷 Mano de obra"
            hint="Pago por horas trabajadas en este lote"
            value={cop(distribucion.manoObra)}
            tone="negative"
          />
          <Row
            label="🌽 Reinversión insumos"
            hint="Maíz, gas y bolsas consumidos a reponer"
            value={cop(distribucion.reinversion)}
            tone="negative"
          />
          <Row
            label="💡 Servicios (agua + luz)"
            hint="Prorrateado — se paga en factura mensual"
            value={cop(distribucion.servicios)}
            muted
          />
          <Row
            label="🏠 Costos fijos prorrateados"
            hint="Arriendo + depreciación repartidos por paquete"
            value={cop(distribucion.costosFijos)}
            muted
          />
          <Row
            label={distribucion.ganancia >= 0 ? '💵 Ganancia neta' : '⚠ Pérdida neta'}
            value={cop(distribucion.ganancia)}
            tone={distribucion.ganancia >= 0 ? 'positive' : 'negative'}
            strong
          />
        </Seccion>
      )}
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold tracking-wide uppercase">
        {titulo}
      </div>
      <div className="divide-y divide-slate-100 bg-white">{children}</div>
    </div>
  );
}

type Tone = 'positive' | 'negative' | 'warn' | undefined;

function Row({
  label,
  value,
  hint,
  tone,
  strong,
  muted,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: Tone;
  strong?: boolean;
  muted?: boolean;
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-emerald-700'
      : tone === 'negative'
        ? 'text-red-600'
        : tone === 'warn'
          ? 'text-amber-600'
          : muted
            ? 'text-slate-500'
            : 'text-slate-900';
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <div className="flex-1 min-w-0">
        <div className={`${strong ? 'font-semibold' : ''} ${muted ? 'text-slate-500' : 'text-slate-700'}`}>
          {label}
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <div className={`text-right tabular-nums ${strong ? 'font-semibold' : ''} ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}
